import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    MenuItem,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import config from "../../config/config";
import { getAuthToken } from "../../utils/token";

//const API = `${config.API}/manager`;
const now = new Date();

const getMonthYear = (planMonth) => {
    if (!planMonth) {
        return {
            month: now.getMonth() + 1,
            year: now.getFullYear(),
        };
    }

    const date = new Date(`${String(planMonth).slice(0, 10)}T00:00:00`);

    if (Number.isNaN(date.getTime())) {
        return {
            month: now.getMonth() + 1,
            year: now.getFullYear(),
        };
    }

    return {
        month: date.getMonth() + 1,
        year: date.getFullYear(),
    };
};

const getErrorMessage = (err, fallback) => {
    const responseMessage = err.response?.data?.message;
    const validationErrors = err.response?.data?.errors;

    if (validationErrors) {
        const firstError = Object.values(validationErrors).flat()[0];
        if (firstError) return firstError;
    }

    return responseMessage || err.message || fallback;
};

export default function ProductionPlanManager() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [open, setOpen] = useState(false);
    const [selected, setSelected] = useState(null);
    const [form, setForm] = useState({
        month: now.getMonth() + 1,
        year: now.getFullYear(),
        qty: "",
    });
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");

    const headers = {
        Authorization: `Bearer ${getAuthToken()}`,
        Accept: "application/json",
    };

    const loadRows = useCallback(async () => {
        setLoading(true);
        setError("");

        try {
            const response = await axios.get(`${config.API}/manager/productionPlanIndex`, {
                headers,
            });

            const data = response.data?.data || [];

            setRows(
                data.map((row) => ({
                    ...row,
                    // Existing plans use their plan ID; unplanned PO items use item ID.
                    id: row.production_plan_id
                        ? `plan-${row.production_plan_id}`
                        : `item-${row.purchase_order_item_id}`,
                }))
            );
        } catch (err) {
            setError(getErrorMessage(err, "Unable to load production plans."));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadRows();
    }, [loadRows]);

    const openForm = (row, edit = false) => {
        const period = edit
            ? getMonthYear(row.plan_month)
            : {
                  month: now.getMonth() + 1,
                  year: now.getFullYear(),
              };

        setSelected(row);
        setForm({
            ...period,
            qty: edit ? row.target_qty ?? "" : "",
        });
        setError("");
        setSuccess("");
        setOpen(true);
    };

    const closeForm = () => {
        if (saving) return;
        setOpen(false);
        setSelected(null);
        setError("");
    };

    const savePlan = async () => {
        setError("");

        if (!selected) {
            setError("Please select a purchase order item.");
            return;
        }

        const qty = Number(form.qty);
        const month = Number(form.month);
        const year = Number(form.year);

        if (
            !Number.isFinite(qty) ||
            qty <= 0 ||
            qty > 50000 ||
            !Number.isInteger(month) ||
            month < 1 ||
            month > 12 ||
            !Number.isInteger(year) ||
            year < 2000 ||
            year > 2100
        ) {
            setError("Enter a valid quantity (1–50000), month and year.");
            return;
        }

        if (!selected.purchase_order_item_id) {
            setError("Purchase order item ID is missing from the API response.");
            return;
        }

        setSaving(true);

        try {
            const response = await axios.post(
                `${config.API}/manager/saveProductionPlan`,
                {
                    purchase_order_item_id: Number(
                        selected.purchase_order_item_id
                    ),
                    po_id: Number(selected.po_id),
                    product_id: Number(selected.product_id),
                    month,
                    year,
                    qty,
                },
                { headers }
            );

            if (response.data?.success === false) {
                throw new Error(
                    response.data.message || "Unable to save production plan."
                );
            }

            setOpen(false);
            setSelected(null);
            setSuccess(
                response.data?.message ||
                    "Production plan saved successfully."
            );

            await loadRows();
        } catch (err) {
            setError(
                getErrorMessage(err, "Unable to save production plan.")
            );
        } finally {
            setSaving(false);
        }
    };

    const columns = [
        {
            field: "po_number",
            headerName: "PO Number",
            minWidth: 220,
            flex: 1,
        },
       // {
        //    field: "purchase_order_item_id",
        //    headerName: "PO Item ID",
        //    width: 115,
      //  },
        {
            field: "product_name",
            headerName: "Item",
            minWidth: 180,
            flex: 1,
        },
       
        {
            field: "consignee",
            headerName: "Consignee",
            minWidth: 180,
            flex: 1,
        },
      /*  {
            field: "plan_month",
            headerName: "Plan Month",
            width: 135,
            valueFormatter: (value) =>
                value
                    ? new Date(
                          `${String(value).slice(0, 10)}T00:00:00`
                      ).toLocaleDateString("en-IN", {
                          month: "short",
                          year: "numeric",
                      })
                    : "Not planned",
        },
        {
            field: "target_qty",
            headerName: "Target Qty",
            width: 110,
            type: "number",
        },
        {
            field: "qty_pending",
            headerName: "Pending Qty",
            width: 115,
            type: "number",
        },
        {
            field: "qty_produced",
            headerName: "Produced",
            width: 100,
            type: "number",
        },*/
        {
            field: "qty_delivered",
            headerName: "Delivered",
            width: 105,
            type: "number",
        },
        {
            field: "actions",
            headerName: "Actions",
            width: 150,
            sortable: false,
            filterable: false,
            renderCell: (params) => (
                <Stack direction="row" spacing={1}>
                    {params.row.production_plan_id ? (
                        <Button
                            size="small"
                            variant="outlined"
                            onClick={() => openForm(params.row, true)}
                        >
                            Edit
                        </Button>
                    ) : (
                        <Button
                            size="small"
                            variant="contained"
                            onClick={() => openForm(params.row, false)}
                        >
                            Add Plan
                        </Button>
                    )}
                </Stack>
            ),
        },
    ];

    return (
        <Box sx={{ p: 2 }}>
            <Stack
                direction={{ xs: "column", sm: "row" }}
                justifyContent="space-between"
                alignItems={{ xs: "stretch", sm: "center" }}
                spacing={2}
                sx={{ mb: 2 }}
            >
                <Typography variant="h5" fontWeight={600}>
                    Production Planning
                </Typography>

                <Button onClick={loadRows} disabled={loading || saving}>
                    Refresh
                </Button>
            </Stack>

            {error && !open && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            {success && (
                <Alert
                    severity="success"
                    sx={{ mb: 2 }}
                    onClose={() => setSuccess("")}
                >
                    {success}
                </Alert>
            )}

            <Box sx={{ height: 620, width: "100%" }}>
                <DataGrid
                    rows={rows}
                    columns={columns}
                    loading={loading}
                    density="compact"
                    disableRowSelectionOnClick
                    pageSizeOptions={[10, 25, 50]}
                    initialState={{
                        pagination: {
                            paginationModel: {
                                page: 0,
                                pageSize: 10,
                            },
                        },
                    }}
                />
            </Box>

            <Dialog
                open={open}
                onClose={closeForm}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle>
                    {selected?.production_plan_id
                        ? "Edit Production Plan"
                        : "Add Production Plan"}
                </DialogTitle>

                <DialogContent>
                    {error && (
                        <Alert severity="error" sx={{ mt: 1 }}>
                            {error}
                        </Alert>
                    )}

                    <Stack spacing={2} sx={{ mt: 2 }}>
                        <TextField
                            label="Purchase Order"
                            value={selected?.po_number || ""}
                            disabled
                            fullWidth
                        />

                        <TextField
                            label="PO Item ID"
                            value={selected?.purchase_order_item_id || ""}
                            disabled
                            fullWidth
                        />

                        <TextField
                            label="Product"
                            value={selected?.product_name || ""}
                            disabled
                            fullWidth
                        />

                        <TextField
                            select
                            label="Month"
                            value={form.month}
                            onChange={(e) =>
                                setForm((prev) => ({
                                    ...prev,
                                    month: Number(e.target.value),
                                }))
                            }
                            fullWidth
                        >
                            {Array.from({ length: 12 }, (_, i) => (
                                <MenuItem key={i + 1} value={i + 1}>
                                    {new Date(2000, i, 1).toLocaleString(
                                        "en",
                                        { month: "long" }
                                    )}
                                </MenuItem>
                            ))}
                        </TextField>

                        <TextField
                            label="Year"
                            type="number"
                            value={form.year}
                            onChange={(e) =>
                                setForm((prev) => ({
                                    ...prev,
                                    year: e.target.value,
                                }))
                            }
                            inputProps={{ min: 2000, max: 2100 }}
                            fullWidth
                        />

                        <TextField
                            label="Target Quantity"
                            type="number"
                            value={form.qty}
                            onChange={(e) =>
                                setForm((prev) => ({
                                    ...prev,
                                    qty: e.target.value,
                                }))
                            }
                            inputProps={{ min: 1, max: 50000, step: 1 }}
                            required
                            fullWidth
                        />
                    </Stack>
                </DialogContent>

                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={closeForm} disabled={saving}>
                        Cancel
                    </Button>

                    <Button
                        variant="contained"
                        onClick={savePlan}
                        disabled={saving}
                    >
                        {saving ? "Saving..." : "Save Production Plan"}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
